import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import '../global.css';
import { useAuth } from '../hooks/useAuth';
import { NO_MATCH_FIELD, RequestHistoryDomain } from '../lib/request-history';
import { EnrichedTranslationsModel } from '../types';
import { Nav } from './Nav';
import { TranslationLine } from './TranslationLine';
import { UsedInAreaFilters } from './UsedInAreaFilters';

export const ManualTranslations = () => {
	const [searchLocale, setSearchLocale] = useState('fr-FR');
	const [lastId, setLastId] = useState<string | undefined>(undefined);
	const [requestHistory, setRequestHistory] =
		useState<RequestHistoryDomain | ''>('');
	const [translationType, setTranslationType] = useState('');

	const [searchResults, setSearchResults] = useState<
		EnrichedTranslationsModel[]
	>([]);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const isFirstRender = useRef(true);

	const { authFetch } = useAuth({ scopes: [] });

	const firstPage = () => {
		setLastId(undefined);
	};
	const nextPage = () => {
		setLastId(searchResults[searchResults.length - 1].translationId);
	};
	// setLastId's state update is async, so refetch only if it was already
	// undefined - otherwise the [lastId] effect below triggers the refetch
	// once the reset has actually taken effect.
	const search = () => {
		setLastId((prev) => {
			if (prev === undefined) void refetch();
			return undefined;
		});
	};

	const queryClient = useQueryClient();

	const { data, refetch, isError, error, isFetching } = useQuery<
		EnrichedTranslationsModel[]
	>({
		queryKey: [
			'requiredTranslations',
			searchLocale,
			lastId ?? '',
			requestHistory || '',
			translationType || '',
		],
		queryFn: async ({ signal }) => {
			const params = new URLSearchParams({ locale: searchLocale });
			if (lastId) {
				params.set('lastId', lastId);
			}
			if (requestHistory) {
				params.set('requestHistory', requestHistory);
			}
			if (translationType) {
				params.set('translationType', translationType);
			}

			const response = await authFetch(
				`/api/overrides/required?${params.toString()}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
					signal,
				}
			);
			if (!response.ok) throw new Error(response.statusText);
			return await response.json();
		},
		retry: false,
		enabled: false,
		gcTime: 0,
	});



	useEffect(() => {
		if (isError) {
			if (error.message.includes('Unauthorized')) {
				setErrorMessage('Authentication failed, please log out and back in');
				return;
			}

			setErrorMessage(error.message);
			return;
		}

		setErrorMessage(null);

		if (data) {
			setSearchResults(data);
		} else {
			setSearchResults([]);
		}
	}, [data, isError]);

	useEffect(() => {
		// the token isn't available on mount, so only paginate on user action
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		void refetch();
	}, [lastId]);

	return (
		<>
			<Nav />
			<main>
				<form
					className="search-toolbar"
					onSubmit={(e) => {
						e.preventDefault();
						search();
					}}
				>
					<input
						className="search-locale"
						type="text"
						value={searchLocale}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && e.ctrlKey) search();
						}}
						onChange={(e) => {
							setSearchLocale(e.target.value);
						}}
					></input>
					<UsedInAreaFilters
						requestHistory={requestHistory}
						onRequestHistoryChange={setRequestHistory}
						translationType={translationType}
						onTranslationTypeChange={setTranslationType}
					/>
					<button type="submit" disabled={isFetching}>
					{isFetching ? 'Fetching...' : 'Go'}
				</button>
				{isFetching && (
					<button
						type="button"
						onClick={() => queryClient.cancelQueries({ queryKey: ['requiredTranslations'] })}
					>
						Cancel
					</button>
				)}
				</form>

				{errorMessage && <p className="error">{errorMessage}</p>}

				{searchResults.map(
					({
						translationId,
						translations,
						overrides,
						requestHistory,
						ColourCode,
						SizeCode,
						AttributeID,
						TranslationType,
						Title,
						NoProductMatch,
					}) => {
						const isProductDomain = requestHistory.includes('ProductDomain');
						const domainsText = isProductDomain
							? 'Product'
							: requestHistory.join(', ');

						const usedInFields = [
							isProductDomain && TranslationType
								? ['Area', TranslationType]
								: null,
							isProductDomain && ColourCode ? ['ColourCode', ColourCode] : null,
							isProductDomain && Title ? ['Name', Title] : null,
							isProductDomain && SizeCode ? ['SizeCode', SizeCode] : null,
							isProductDomain && AttributeID
								? ['AttributeID', AttributeID]
								: null,
							isProductDomain && NoProductMatch
								? NO_MATCH_FIELD
								: null,
						].filter((field): field is [string, string] => field !== null);

						return (
							<section key={translationId}>
								<h2 className="used-in">
									<span className="used-in-label">Used in:</span>{' '}
									<span className="used-in-value">{domainsText}</span>
									{usedInFields.map(([label, value]) => (
										<span className="used-in-field" key={label}>
											<span className="used-in-label">{label}:</span>{' '}
											<span className="used-in-value">{value}</span>
										</span>
									))}
								</h2>
								{Object.entries(translations)
									.filter(
										([locale]) => locale === 'en-GB' || locale === searchLocale
									)
									.map(([locale, text]) => (
										<TranslationLine
											refetch={refetch}
											key={translationId + locale}
											locale={locale}
											translation={text}
											override={overrides?.[locale]?.text}
											translationId={translationId}
											isOverridable={locale !== 'en-GB'}
											canSaveOriginalAsOverride={true}
										/>
									))}
							</section>
						);
					}
				)}
				<div className="pagination-actions">
					{lastId && (
						<button className="page-action" onClick={() => firstPage()}>
							back to first page
						</button>
					)}
					{searchResults.length === 20 && (
						<button className="page-action" onClick={() => nextPage()}>
							next page
						</button>
					)}
				</div>
			</main>
		</>
	);
};
